import {
  APPLE_PAYMENT_ERROR_TEXT,
  AUTO_PAY_CANCELED_SUCCESS_MESSAGE,
  PAYMENT_ERROR_MESSAGE,
  SVG_SIZE_M,
} from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import { MinusCircle } from '@phosphor-icons/react';
import { useUpdateCreditAccountMutation } from 'src/store/services';
import { toast } from 'react-toastify';
import { TokenApiHttpErrorWithSeveralErrorMessages } from 'src/types';
import { CommonDeleteModal } from 'src/components/CommonDeleteModal';
import { useCreditsAccountInfo, useSession, useVisible } from 'src/hooks';
import { useState } from 'react';
import styles from './CancelAutoPayForCreditsButton.module.scss';

type CancelAutoPayForCreditsButtonProps = {
  isAppleOrAmpPayment?: boolean;
  onClose: () => void;
};

export const CancelAutoPayForCreditsButton = ({
  isAppleOrAmpPayment,
  onClose,
}: CancelAutoPayForCreditsButtonProps) => {
  const {
    appUser: { user_id },
  } = useSession();
  const { creditAccountInfo } = useCreditsAccountInfo();
  const [isCancelAutoPayIsLoading, setIsCancelAutoPayIsLoading] =
    useState<boolean>(false);
  const {
    isVisible: isCancelAutoPayModalVisible,
    onVisibilitySet: onCancelAutoPayModalVisibilitySet,
    onVisibilityRemove: onCancelAutoPayModalVisibilityRemove,
  } = useVisible();

  const [updateCreditAccount] = useUpdateCreditAccountMutation();

  const handleCancelAutoPayCreditsSubmitClick = async () => {
    try {
      setIsCancelAutoPayIsLoading(true);
      await updateCreditAccount({
        user_id,
        credit_account: creditAccountInfo?.account_id || '',
        paymentInfo: {
          reload_config: {
            auto_reload_enabled: false,
            reload_amount_nanos: null,
            reload_threshold_amount_nanos: null,
          },
        },
      }).unwrap();
      toast.success(AUTO_PAY_CANCELED_SUCCESS_MESSAGE);
    } catch (e) {
      toast.error(
        (e as TokenApiHttpErrorWithSeveralErrorMessages)?.data?.detail
          ? (e as TokenApiHttpErrorWithSeveralErrorMessages).data.detail[0]
              ?.msg || ''
          : PAYMENT_ERROR_MESSAGE,
      );
    } finally {
      setIsCancelAutoPayIsLoading(false);
      onCancelAutoPayModalVisibilityRemove();
      onClose();
    }
  };

  return (
    <>
      <Button
        color="transparent"
        tooltipContent={isAppleOrAmpPayment ? APPLE_PAYMENT_ERROR_TEXT : ''}
        onClick={onCancelAutoPayModalVisibilitySet}
      >
        <MinusCircle size={SVG_SIZE_M} />
        <span>Cancel auto-pay for credits</span>
      </Button>
      <CommonDeleteModal
        isOpen={isCancelAutoPayModalVisible}
        onClose={onCancelAutoPayModalVisibilityRemove}
        onSubmit={handleCancelAutoPayCreditsSubmitClick}
        isLoading={isCancelAutoPayIsLoading}
        title="Delete Auto-pay"
        cancelButtonTitle="Keep Auto-pay"
        deleteButtonTitle="Delete Auto-pay"
        customBody={
          <p className={styles.modalBody}>
            Are you sure you want to delete your autopay? Your API will stop
            working once credits run out. To restore access, you'll need to
            create a new autopay.
          </p>
        }
      />
    </>
  );
};
